import React, { useEffect, useRef, useState } from 'react';
import './landing.css';
import SignupForm from './SignupForm';
import Picture from './Picture';
import Icon from './Icon';
import { logData } from '../../api';
import { trackEvent } from './track';
import {
    STEPS, STAGES, LESSONS, IN_ACTION, LESSON_PAGES, COMPANIES_URL, SITE_URL, PREVIEW_LESSON, PREVIEW_PDF_URL,
    QUOTE, QUOTE_AFTER, TESTIMONIALS, FAQ,
} from './content';

const VIDEO_ID = "t39dhOCSNSE";

const HERO_FORM_ID = "formular-sus";
const FINAL_FORM_ID = "formular-final";

// Duce omul la cel mai apropiat dintre cele două formulare. Folosim scrollIntoView, nu href="#...",
// pentru că HashRouter ar trata ancora ca pe o rută.
const scrollToNearestForm = () => {
    const forms = [HERO_FORM_ID, FINAL_FORM_ID].map((id) => document.getElementById(id)).filter(Boolean);
    if (!forms.length) return;
    const middle = window.innerHeight / 2;
    const distance = (el) => Math.abs(el.getBoundingClientRect().top + el.offsetHeight / 2 - middle);
    const el = forms.reduce((best, f) => (distance(f) < distance(best) ? f : best));
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    const input = el.querySelector('input');
    if (input) setTimeout(() => input.focus({ preventScroll: true }), 500);
};

const MidCta = ({ id }) => (
    <div className="lp-midcta">
        <button
            type="button"
            className="lp-btn lp-btn--cta"
            onClick={() => {
                logData("midCta", { id: id }).catch(() => { });
                trackEvent('cta_click', { location: id });
                scrollToNearestForm();
            }}
        >
            Trimite-mi Lecția 1
        </button>
    </div>
);

const Hero = ({ formRef, onSubmitted, submittedBy }) => (
    <section className="lp-hero">
        <div className="lp-container lp-hero__grid">
            <div className="lp-hero__text">
                <h1 className="lp-h1">AI-ul, explicat pe&nbsp;românește.</h1>
                <p className="lp-hero__intro">
                    <Picture name="avatar" widths={[160]} sizes="52px" alt="" className="lp-eyebrow__avatar" width="52" height="52" eager />
                    <span>
                        <b>Curs gratuit de AI, prin email.</b>
                        <span className="lp-hero__intro-line">Primești o lecție pe săptămână.</span>
                        <span className="lp-hero__intro-line">Nu e momeală. Nu-ți cer bani. Nici acum, nici mai târziu.</span>
                    </span>
                </p>
                <div className="lp-hero__form" ref={formRef} id={HERO_FORM_ID}>
                    <SignupForm id="hero" onSubmitted={onSubmitted} otherDone={submittedBy === 'final'} />
                </div>
                {!submittedBy && (
                    <p className="lp-next">Lecția 1 (PDF) vine acum pe email, apoi câte una pe săptămână.</p>
                )}
            </div>
            <div className="lp-hero__media">
                <div className="lp-hero__photo">
                    {/* Doar pe ecrane mari: pe telefon <img> rămâne cu un pixel gol, deci portretul nu se descarcă. */}
                    <picture>
                        <source media="(min-width: 992px)" type="image/webp" srcSet="assets/v2/hero-portrait-480.webp 480w, assets/v2/hero-portrait-800.webp 800w, assets/v2/hero-portrait-1100.webp 1100w" sizes="440px" />
                        <source media="(min-width: 992px)" srcSet="assets/v2/hero-portrait-480.jpg 480w, assets/v2/hero-portrait-800.jpg 800w, assets/v2/hero-portrait-1100.jpg 1100w" sizes="440px" />
                        <img src="data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw==" alt="Viorel Spînu" width="800" height="1000" />
                    </picture>
                    <div className="lp-hero__covers" aria-hidden="true">
                        <img src="assets/v2/cover-1-260.webp" alt="" width="130" height="161" loading="lazy" />
                        <img src="assets/v2/cover-22-260.webp" alt="" width="130" height="161" loading="lazy" />
                    </div>
                </div>
            </div>
        </div>
    </section>
);

const ProofStrip = () => (
    <section className="lp-proof" aria-label="Unde am vorbit despre AI">
        <div className="lp-container">
            <p>
                <span>20 de ani de experiență practică în tehnologii emergente și AI <a className="lp-proof__link" href="https://www.linkedin.com/in/viorelspinu/" target="_blank" rel="noopener noreferrer">vezi pe LinkedIn →</a></span>
                <span className="lp-dot" aria-hidden="true">·</span>
                <span>Speaker la conferințe despre AI și la TV <a className="lp-proof__link" href="https://www.viorelspinu.com/2025/04/ai-on-stage-conference-speaker.html" target="_blank" rel="noopener noreferrer">vezi pe blog →</a></span>
            </p>
        </div>
    </section>
);

const WhyWeekly = () => (
    <section className="lp-section lp-section--cream" data-track="de-ce-saptamanal">
        <div className="lp-container">
            <h2 className="lp-h2">De ce o singură lecție pe săptămână</h2>
            <div className="lp-story">
                <p>
                    Informații despre AI găsești oriunde: clipuri, postări, tutoriale. Le salvezi „pentru mai târziu”
                    și rămân necitite. <b>Ce lipsește nu e informația, ci drumul</b>: ce înveți întâi, ce urmează
                    și în ce ritm. Asta îți dau eu.
                </p>
                <p>
                    Urmăresc ce apare nou în AI, e meseria mea. În lecții pun <b>doar ce te ajută la treaba de zi cu zi</b>,
                    explicat pe românește. Și le pun <b>în ordine</b>: fiecare lecție pleacă de unde a rămas cea dinainte,
                    ca să nu mai sari de la un tutorial la altul.
                </p>
                <p>
                    Nu trebuie să ții tu minte să intri nicăieri. <b>Lecția vine singură în inbox</b>, în fiecare săptămână,
                    și o citești când ai timp. Ai sărit o săptămână? Nu-i nimic: <b>te așteaptă acolo</b> și mergi mai departe.
                </p>
            </div>
        </div>
    </section>
);

const CoursePath = () => (
    <section className="lp-section lp-section--cream lp-path" data-track="ce-inveti">
        <div className="lp-container">
            <h2 className="lp-h2">Ce înveți</h2>
            <p className="lp-section__intro">
                Începi cu bazele, apoi treci la treburile de zi cu zi de la birou. Câteva lecții din curs, pe teme:
            </p>
            <div className="lp-stages">
                {STAGES.map((st, i) => (
                    <div className="lp-stage" key={st.name}>
                        <p className="lp-stage__name"><span>{i + 1}</span>{st.name}</p>
                        <p className="lp-stage__text">{st.text}</p>
                        <ul className="lp-stage__list">
                            {st.lessons.map((n) => (
                                <li key={n}><span className="lp-stage__n">Lecția {n}</span>{LESSONS[n - 1]}</li>
                            ))}
                        </ul>
                    </div>
                ))}
            </div>
            <details
                className="lp-all"
                onToggle={(e) => { if (e.currentTarget.open) trackEvent('all_lessons_open', {}); }}
            >
                <summary>Vezi toate cele {LESSONS.length} de lecții</summary>
                <ol className="lp-all__list">
                    {LESSONS.map((t, i) => <li key={i}>{t}</li>)}
                </ol>
            </details>
            <MidCta id="path" />
        </div>
    </section>
);

const Steps = () => (
    <section className="lp-section" data-track="cum-functioneaza">
        <div className="lp-container">
            <h2 className="lp-h2">Primești o lecție în fiecare săptămână, prin email</h2>
            <ol className="lp-flow">
                {STEPS.map((s, i) => (
                    <li key={i} className="lp-flow__step">
                        <span className="lp-flow__icon"><Icon name={s.icon} size={30} /></span>
                        <span className="lp-flow__num">Pasul {i + 1}</span>
                        <p className="lp-flow__title">{s.title}</p>
                        <p className="lp-flow__text">{s.text}</p>
                    </li>
                ))}
            </ol>
        </div>
    </section>
);

const LessonPreview = () => {
    const [open, setOpen] = useState(null);
    const [pdfUrl, setPdfUrl] = useState(PREVIEW_PDF_URL);
    const closeRef = useRef(null);
    const returnFocusRef = useRef(null);

    useEffect(() => {
        if (!open) return;
        const onKey = (e) => {
            if (e.key === 'Escape') setOpen(null);
            // Singurul element focusabil e butonul de închidere, deci Tab rămâne pe el.
            if (e.key === 'Tab') { e.preventDefault(); closeRef.current && closeRef.current.focus(); }
        };
        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        closeRef.current && closeRef.current.focus();
        window.addEventListener('keydown', onKey);
        return () => {
            window.removeEventListener('keydown', onKey);
            document.body.style.overflow = previousOverflow;
            returnFocusRef.current && returnFocusRef.current.focus();
        };
    }, [open]);

    // Numele PDF-ului se schimbă când lecția e refăcută, așa că îl luăm din catalogul public al lecțiilor.
    useEffect(() => {
        fetch(SITE_URL + "/lessons.json")
            .then((r) => r.json())
            .then((d) => { const l = d && d[String(PREVIEW_LESSON)]; if (l && l.file) setPdfUrl(SITE_URL + "/" + l.file); })
            .catch(() => { });
    }, []);

    const show = (p) => {
        returnFocusRef.current = document.activeElement;
        setOpen(p);
        logData("openSummary", { referrer: document.referrer || "No referrer", state: p.id }).catch(() => { });
    };

    return (
        <section className="lp-section" data-track="lectie">
            <div className="lp-container">
                <h2 className="lp-h2">Cum arată o lecție</h2>
                <p className="lp-section__intro">
                    Uite Lecția&nbsp;2, „Cum apare un răspuns din numere”. Afli:
                </p>
                <ul className="lp-lesson-points">
                    <li>ce se întâmplă în spate când îi scrii lui ChatGPT;</li>
                    <li>de ce două răspunsuri la aceeași întrebare pot ieși diferite;</li>
                    <li>de ce un răspuns care sună convingător poate fi totuși greșit.</li>
                </ul>
                <div className="lp-preview">
                    <button type="button" className="lp-excerpt" onClick={() => show({ id: "l2-p11", alt: "Pagina 11 din Lecția 2: de ce un răspuns fluent poate conține informații false" })} aria-label="Mărește pagina din Lecția 2">
                        <Picture name="l2-excerpt" widths={[755]} sizes="(min-width: 992px) 640px, 100vw" alt="Fragment din Lecția 2: de ce un răspuns fluent poate conține informații false" width="755" height="794" />
                    </button>
                    <p className="lp-excerpt__hint">Apasă pe pagină ca s-o mărești.</p>
                    <div className="lp-preview__side">
                        <p className="lp-preview__note">Alte pagini din lecție. Apasă ca să le mărești:</p>
                        <div className="lp-pages">
                            {LESSON_PAGES.map((p) => (
                                <button type="button" className="lp-page" key={p.id} onClick={() => show(p)} aria-label={`Mărește: ${p.alt}`}>
                                    <Picture name={`${p.id}-thumb`} widths={[260]} sizes="(min-width: 992px) 150px, 30vw" alt={p.alt} width="260" height="369" />
                                </button>
                            ))}
                        </div>
                        <div className="lp-download">
                            <a
                                className="lp-btn lp-btn--ghost lp-download__btn"
                                href={pdfUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                download={`CursAIViorelSpinu-Lectia_${PREVIEW_LESSON}.pdf`}
                                onClick={() => {
                                    logData("downloadLesson", { lesson: PREVIEW_LESSON, referrer: document.referrer || "No referrer" }).catch(() => { });
                                    trackEvent('file_download', { file_name: `Lectia_${PREVIEW_LESSON}.pdf`, link_location: 'lectie' });
                                }}
                            >
                                <Icon name="download" size={22} /> Descarcă Lecția 2 întreagă (PDF)
                            </a>
                            <p className="lp-download__note">
                                3,6 MB, fără înscriere.
                            </p>
                        </div>
                        <figure className="lp-quote">
                            <blockquote>{QUOTE}</blockquote>
                            <figcaption>{QUOTE_AFTER}</figcaption>
                        </figure>
                    </div>
                </div>
            </div>
            {open && (
                <div className="lp-lightbox" role="dialog" aria-modal="true" aria-label={open.alt} onClick={() => setOpen(null)}>
                    <button type="button" className="lp-lightbox__close" aria-label="Închide" ref={closeRef} onClick={() => setOpen(null)}>×</button>
                    {/* Clicul pe pagină nu închide fereastra, ca omul să poată mări cu degetele; se închide din × sau din fundal. */}
                    <div className="lp-lightbox__page" onClick={(e) => e.stopPropagation()}>
                        <Picture name={open.id} widths={[880]} sizes="(min-width: 900px) 880px, 100vw" alt={open.alt} width="880" height="1248" eager />
                    </div>
                </div>
            )}
        </section>
    );
};

const VideoThumb = () => {
    const [playing, setPlaying] = useState(false);
    const frameRef = useRef(null);
    useEffect(() => { if (playing && frameRef.current) frameRef.current.focus(); }, [playing]);
    if (playing) {
        return (
            <div className="lp-video">
                <iframe
                    ref={frameRef}
                    src={`https://www.youtube-nocookie.com/embed/${VIDEO_ID}?autoplay=1`}
                    title="Viorel Spînu: cum funcționează un sistem cu inteligență artificială"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    referrerPolicy="strict-origin-when-cross-origin"
                    allowFullScreen
                />
            </div>
        );
    }
    return (
        <button type="button" className="lp-video lp-video--thumb" onClick={() => setPlaying(true)} aria-label="Pornește clipul">
            <Picture name="video-thumb" widths={[640, 1100]} sizes="(min-width: 992px) 520px, 100vw" alt="" width="1100" height="619" />
            <span className="lp-video__play"><Icon name="play" size={64} /></span>
        </button>
    );
};

const About = () => (
    <section className="lp-section lp-section--dark" id="despre" data-track="despre">
        <div className="lp-container lp-about">
            <div className="lp-about__text">
                <h2 className="lp-h2">Cine sunt eu</h2>
                <p className="lp-about__lead">
                    Sunt Viorel Spînu. Lucrez de 20 de ani în tehnologii emergente și AI. Sunt inginer și construiesc
                    sisteme AI, iar în paralel le explic pe românește: la conferințe, la TV, în workshopuri pentru firme
                    și în cursul ăsta.
                </p>
                <div className="lp-about__links">
                    <a className="lp-btn lp-btn--ghost-light" href="https://www.linkedin.com/in/viorelspinu/" target="_blank" rel="noopener noreferrer">LinkedIn →</a>
                    <a className="lp-btn lp-btn--ghost-light" href="https://viorelspinu.com/" target="_blank" rel="noopener noreferrer">viorelspinu.com →</a>
                </div>
            </div>
            <div className="lp-about__media">
                <VideoThumb />
                <p className="lp-video__caption">Un clip scurt: cum funcționează un sistem cu AI.</p>
            </div>
        </div>
        <InAction />
    </section>
);

// După „Mai multe…”, mutăm focusul pe primul element apărut, ca tastatura și cititorul de ecran să nu o ia de la capăt.
const focusFirstHidden = (selector) => {
    requestAnimationFrame(() => {
        const el = document.querySelector(selector);
        if (el) el.focus({ preventScroll: true });
    });
};

// Pe telefon arătăm întâi 12 apariții (pragul e în CSS); restul după un clic.

const InAction = () => {
    const [all, setAll] = useState(false);
    return (
        <div className={`lp-container lp-inaction ${all ? 'is-all' : ''}`}>
            <h3 className="lp-inaction__title">Unde am vorbit despre AI</h3>
            <p className="lp-inaction__intro">Apasă pe o imagine ca să vezi articolul de pe blog.</p>
            <ul className="lp-inaction__grid">
                {IN_ACTION.map((it) => (
                    <li key={it.img}>
                        <a href={it.href} target="_blank" rel="noopener noreferrer">
                            <picture>
                                <source type="image/webp" srcSet={`assets/v2/inaction/${it.img}.webp`} />
                                <img src={`assets/v2/inaction/${it.img}.jpg`} alt={it.alt} width="260" height="260" loading="lazy" decoding="async" />
                            </picture>
                        </a>
                    </li>
                ))}
            </ul>
            {!all && (
                <button type="button" className="lp-linkbtn lp-inaction__more" onClick={() => { setAll(true); focusFirstHidden('.lp-inaction__grid li:nth-child(13) a'); }}>
                    Vezi toate cele {IN_ACTION.length} de apariții +
                </button>
            )}
        </div>
    );
};

// Pe telefon arătăm întâi doar câteva mesaje, ca secțiunea să nu fie interminabilă.
const VOICES_ON_PHONE = 8;

// Câte coloane are zidul de mesaje la lățimea curentă (aceleași praguri ca în CSS).
const useVoiceColumns = () => {
    const count = () => (window.innerWidth >= 992 ? 3 : window.innerWidth >= 640 ? 2 : 1);
    const [cols, setCols] = useState(count);
    useEffect(() => {
        const onResize = () => setCols(count());
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, []);
    return cols;
};

// Pune fiecare mesaj în coloana cea mai scurtă de până atunci, ca lungimile să se amestece.
// Primele mesaje rămân pe primul rând.
const splitIntoColumns = (items, cols) => {
    const columns = Array.from({ length: cols }, () => ({ items: [], height: 0 }));
    items.forEach((t, i) => {
        const shortest = columns.reduce((best, c) => (c.height < best.height ? c : best));
        shortest.items.push({ ...t, index: i });
        shortest.height += t.text.length + 200; // textul + spațiul fix al casetei
    });
    return columns.map((c) => c.items);
};

const Testimonials = () => {
    const [all, setAll] = useState(false);
    const cols = useVoiceColumns();
    return (
        <section className="lp-section lp-section--voices" data-track="testimoniale">
            <div className="lp-container">
                <h2 className="lp-h2">Ce mi-au scris cei care citesc lecțiile</h2>
                <p className="lp-section__intro">
                    Fragmente din emailurile cursanților, exact cum le-au scris.
                    Fără nume, pentru că mi le-au trimis mie, personal.
                </p>
                <div className={`lp-voices ${all ? 'is-all' : ''}`} style={{ gridTemplateColumns: `repeat(${cols}, 1fr)` }}>
                    {splitIntoColumns(TESTIMONIALS, cols).map((column, c) => (
                        <div className="lp-voices__col" key={c}>
                            {column.map((t, row) => (
                                <figure tabIndex="-1" className={`lp-voice lp-voice--tone${(row + c) % 2} ${t.index >= VOICES_ON_PHONE ? 'lp-voice--more' : ''}`} key={t.index}>
                                    <blockquote>{t.text}</blockquote>
                                    <figcaption>{t.src}</figcaption>
                                </figure>
                            ))}
                        </div>
                    ))}
                </div>
                {!all && (
                    <button type="button" className="lp-linkbtn lp-voices__more" onClick={() => { setAll(true); focusFirstHidden('.lp-voice--more'); }}>
                        Vezi toate cele {TESTIMONIALS.length} de mesaje +
                    </button>
                )}
            </div>
        </section>
    );
};

const Faq = ({ showAskAnythingDialog }) => (
    <section className="lp-section" data-track="intrebari">
        <div className="lp-container lp-container--faq">
            <h2 className="lp-h2">Întrebări frecvente</h2>
            <div className="lp-faq">
                {FAQ.map((f) => (
                    <details key={f.q} onToggle={(e) => { if (e.currentTarget.open) trackEvent('faq_open', { question: f.q }); }}>
                        <summary>{f.q}</summary>
                        <p>{f.a}</p>
                    </details>
                ))}
                <details>
                    <summary>Mai am o întrebare.</summary>
                    <p>
                        <button type="button" className="lp-linkbtn" onClick={showAskAnythingDialog}>Scrie-mi aici</button> și
                        îți răspund eu.
                    </p>
                </details>
            </div>
        </div>
    </section>
);

const FinalCta = ({ formRef, onSubmitted, submittedBy }) => (
    <section className="lp-section lp-section--dark lp-final" id="inscriere" data-track="formular-final">
        <div className="lp-container lp-container--narrow">
            <h2 className="lp-h2">Prima lecție vine imediat, pe email</h2>
            <p className="lp-section__intro">
                Gratuit, mereu. Citești prima lecție și decizi tu dacă merită să continui.
            </p>
            <div ref={formRef} id={FINAL_FORM_ID}>
                <SignupForm id="final" dark onSubmitted={onSubmitted} otherDone={submittedBy === 'hero'} />
            </div>
        </div>
    </section>
);

// Bara de jos, doar pe telefon: apare când formularul de sus nu se mai vede.
const StickyCta = ({ visible }) => (
    <div className={`lp-sticky ${visible ? 'is-visible' : ''}`} aria-hidden={!visible}>
        <button type="button" className="lp-btn lp-btn--cta lp-btn--block" onClick={scrollToNearestForm} tabIndex={visible ? 0 : -1}>
            Trimite-mi Lecția 1
        </button>
    </div>
);

const Companies = ({ showAskAnythingDialog }) => (
    <section className="lp-companies" aria-label="Pentru firme">
        <div className="lp-container lp-companies__inner">
            <div>
                <p className="lp-companies__label">Pentru firme</p>
                <p className="lp-companies__text">
                    Țin workshopuri practice de AI pentru echipe, împreună cu Cristina Anghel, prin AI Leadership.
                </p>
            </div>
            <a
                className="lp-companies__link"
                href={COMPANIES_URL}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => {
                    logData("companiesClick", {}).catch(() => { });
                    trackEvent('companies_click', {});
                }}
            >
                Detalii pe aileadership.ro →
            </a>
            <button
                type="button"
                className="lp-btn lp-btn--ghost lp-companies__write"
                onClick={() => {
                    logData("companiesWrite", {}).catch(() => { });
                    trackEvent('companies_contact', {});
                    showAskAnythingDialog();
                }}
            >
                Scrie-mi despre echipa ta
            </button>
        </div>
    </section>
);

const Landing = ({ showAskAnythingDialog }) => {
    const heroFormRef = useRef(null);
    const finalFormRef = useRef(null);
    const [heroVisible, setHeroVisible] = useState(true);
    const [finalVisible, setFinalVisible] = useState(false);
    const [submittedBy, setSubmittedBy] = useState(null);
    const submitted = submittedBy !== null;

    useEffect(() => {
        if (!('IntersectionObserver' in window)) return;
        const watch = (el, setter) => {
            if (!el) return null;
            const obs = new IntersectionObserver(([entry]) => setter(entry.isIntersecting), { threshold: 0 });
            obs.observe(el);
            return obs;
        };
        const a = watch(heroFormRef.current, setHeroVisible);
        // Ascundem bara și după ce omul a trecut de formularul de jos (zona firme + subsol).
        // Verificăm poziția la derulare, ca să meargă și la salturi bruște (tasta End, tap pe bara iOS).
        let ticking = false;
        const checkFinal = () => {
            ticking = false;
            const el = finalFormRef.current;
            if (el) setFinalVisible(el.getBoundingClientRect().top < window.innerHeight);
        };
        const onScroll = () => { if (!ticking) { ticking = true; window.requestAnimationFrame(checkFinal); } };
        window.addEventListener('scroll', onScroll, { passive: true });
        window.addEventListener('resize', onScroll);
        checkFinal();
        return () => {
            a && a.disconnect();
            window.removeEventListener('scroll', onScroll);
            window.removeEventListener('resize', onScroll);
        };
    }, []);

    const onSubmitted = (id) => setSubmittedBy(id);

    // Cât de departe citesc oamenii: un eveniment o singură dată pentru fiecare secțiune văzută.
    useEffect(() => {
        if (!('IntersectionObserver' in window)) return;
        const seen = new Set();
        const obs = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                const name = entry.target.getAttribute('data-track');
                if (entry.isIntersecting && !seen.has(name)) {
                    seen.add(name);
                    trackEvent('section_view', { section: name });
                }
            });
        }, { rootMargin: '0px 0px -40% 0px' });
        document.querySelectorAll('[data-track]').forEach((el) => obs.observe(el));
        return () => obs.disconnect();
    }, []);

    return (
        <main className="lp">
            <Hero formRef={heroFormRef} onSubmitted={onSubmitted} submittedBy={submittedBy} />
            <ProofStrip />
            <Steps />
            <About />
            <WhyWeekly />
            <CoursePath />
            <LessonPreview />
            <Testimonials />
            <Faq showAskAnythingDialog={showAskAnythingDialog} />
            <FinalCta formRef={finalFormRef} onSubmitted={onSubmitted} submittedBy={submittedBy} />
            <StickyCta visible={!heroVisible && !finalVisible && !submitted} />
            <Companies showAskAnythingDialog={showAskAnythingDialog} />
        </main>
    );
};

export default Landing;
